/**
 * NotificationPreferences — full settings panel for push + in-app notification toggles + reminder time.
 * Lives under the PushNotificationToggle on the Profile page.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, Globe, BellRing, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Common timezones list (a small, curated set — covers 95% of users).
const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Athens",
  "Africa/Lagos",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Singapore",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const PUSH_TOGGLES = [
  {
    key: "push_workout_reminders",
    icon: "💪",
    title: "Workout reminders",
    desc: "Daily ping at your preferred training time",
  },
  {
    key: "push_streak_warnings",
    icon: "🔥",
    title: "Streak warnings",
    desc: "Alert before you break a training streak",
  },
  {
    key: "push_recovery_suggestions",
    icon: "🧘",
    title: "Recovery nudges",
    desc: "Active recovery prompts on rest days",
  },
];

const IN_APP_TOGGLES = [
  { key: "hydration_nudges", title: "Hydration nudges" },
  { key: "badge_unlocks", title: "Badge unlocks" },
  { key: "weekly_summary", title: "Weekly summary" },
  { key: "beast_mode_challenges", title: "Beast Mode challenges" },
];

const hourLabel = (h) => {
  if (h === 0) return "12 AM";
  if (h < 12) return `${h} AM`;
  if (h === 12) return "12 PM";
  return `${h - 12} PM`;
};

const NotificationPreferences = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [smartHour, setSmartHour] = useState(null);
  const detectedTz =
    (typeof Intl !== "undefined" &&
      Intl.DateTimeFormat &&
      Intl.DateTimeFormat().resolvedOptions().timeZone) ||
    "UTC";

  useEffect(() => {
    fetchPrefs();
    fetchSmartHour();
  }, []);

  const fetchSmartHour = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/notifications/smart-time/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmartHour(res.data.smart_hour);
    } catch {
      // ignore
    }
  };

  const fetchPrefs = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrefs(res.data);
    } catch {
      // Fall back to defaults
      setPrefs({
        push_workout_reminders: true,
        push_streak_warnings: true,
        push_recovery_suggestions: true,
        workout_reminder_local_hour: 18,
        timezone: detectedTz,
        hydration_nudges: true,
        badge_unlocks: true,
        weekly_summary: true,
        beast_mode_challenges: false,
      });
    }
  };

  const update = (patch) => setPrefs((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { ...prefs };
      delete payload.user_id;
      delete payload.updated_at;
      await axios.put(`${API}/notifications/preferences`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Notification preferences saved");
    } catch {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Loading preferences...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border" data-testid="notification-prefs-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading text-white flex items-center gap-2">
          <BellRing className="w-5 h-5 text-primary" /> Notification Preferences
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* PUSH TOGGLES */}
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400 mb-3">
            Push Notifications
          </p>
          <div className="space-y-3">
            {PUSH_TOGGLES.map((t) => (
              <div
                key={t.key}
                className="flex items-center justify-between gap-3 p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm"
                data-testid={`pref-${t.key}`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <span className="text-xl">{t.icon}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">{t.title}</p>
                    <p className="text-xs text-zinc-500">{t.desc}</p>
                  </div>
                </div>
                <Switch
                  checked={!!prefs[t.key]}
                  onCheckedChange={(v) => update({ [t.key]: v })}
                  data-testid={`switch-${t.key}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* TIMING */}
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400 mb-3">
            Timing
          </p>

          {/* Smart Time Learning */}
          <div
            className="flex items-center justify-between gap-3 p-3 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/30 rounded-sm mb-3"
            data-testid="pref-smart-time"
          >
            <div className="flex items-start gap-3 min-w-0">
              <Sparkles className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">Smart Time Learning</p>
                <p className="text-xs text-zinc-500">
                  Auto-tune the reminder from your training history
                  {smartHour !== null && smartHour !== undefined ? (
                    <span className="text-orange-400"> · suggesting {hourLabel(smartHour)}</span>
                  ) : (
                    <span className="text-zinc-600"> · need 3+ workouts</span>
                  )}
                </p>
              </div>
            </div>
            <Switch
              checked={!!prefs.smart_time_enabled}
              onCheckedChange={(v) => {
                const patch = { smart_time_enabled: v };
                if (v && smartHour !== null && smartHour !== undefined) {
                  patch.workout_reminder_local_hour = smartHour;
                }
                update(patch);
              }}
              data-testid="switch-smart-time"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm">
              <label className="flex items-center gap-2 text-xs font-bold text-white mb-2">
                <Clock className="w-3.5 h-3.5 text-primary" /> Reminder time (local)
              </label>
              <Select
                value={String(prefs.workout_reminder_local_hour ?? 18)}
                onValueChange={(v) => update({ workout_reminder_local_hour: parseInt(v) })}
                disabled={!!prefs.smart_time_enabled}
              >
                <SelectTrigger data-testid="reminder-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }).map((_, h) => (
                    <SelectItem key={h} value={String(h)}>
                      {hourLabel(h)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {prefs.smart_time_enabled && (
                <p className="text-[10px] text-zinc-500 mt-1.5">Managed by Smart Time</p>
              )}
            </div>

            <div className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm">
              <label className="flex items-center gap-2 text-xs font-bold text-white mb-2">
                <Globe className="w-3.5 h-3.5 text-primary" /> Timezone
              </label>
              <Select
                value={prefs.timezone || detectedTz}
                onValueChange={(v) => update({ timezone: v })}
              >
                <SelectTrigger data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {/* User's auto-detected TZ on top if not in list */}
                  {!TIMEZONES.includes(detectedTz) && (
                    <SelectItem value={detectedTz}>{detectedTz} (auto)</SelectItem>
                  )}
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz} {tz === detectedTz ? "(auto)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {prefs.timezone !== detectedTz && (
                <button
                  onClick={() => update({ timezone: detectedTz })}
                  className="text-[10px] font-mono uppercase tracking-wider text-orange-400 mt-2"
                  data-testid="use-auto-tz-btn"
                >
                  Use device timezone ({detectedTz})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* IN-APP TOGGLES */}
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400 mb-3">
            In-App Notifications (Bell)
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {IN_APP_TOGGLES.map((t) => (
              <div
                key={t.key}
                className="flex items-center justify-between gap-3 p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm"
                data-testid={`pref-${t.key}`}
              >
                <p className="text-sm text-white">{t.title}</p>
                <Switch
                  checked={!!prefs[t.key]}
                  onCheckedChange={(v) => update({ [t.key]: v })}
                  data-testid={`switch-${t.key}`}
                />
              </div>
            ))}
          </div>
        </div>

        <Button
          onClick={save}
          disabled={saving}
          className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider rounded-none"
          data-testid="save-notif-prefs-btn"
        >
          {saving ? "Saving..." : "Save Preferences"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default NotificationPreferences;
